import { useParams, Link, useNavigate } from "react-router-dom";
import { useEffect } from "react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import ContactForm from "../components/ContactForm";
import { vehicles } from "../data/vehicles";
import {
  Calendar,
  Gauge,
  Zap,
  Wind,
  Timer,
  Phone,
  MessageCircle,
  ChevronLeft,
  Check,
} from "lucide-react";

const DetailPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const vehicle = vehicles.find((v) => v.slug === slug);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!vehicle) {
    return (
      <div className="min-h-screen bg-ink-950" data-testid="detail-not-found">
        <Header />
        <div className="max-w-[1440px] mx-auto px-6 md:px-12 py-24 text-center">
          <h1 className="font-display text-4xl text-white mb-3">
            Voertuig niet gevonden
          </h1>
          <p className="text-sm text-zinc-400 font-body mb-8">
            Het door u opgevraagde voertuig is niet meer in onze collectie.
          </p>
          <button
            type="button"
            onClick={() => navigate("/aanbod")}
            className="px-6 py-3 border border-champagne text-champagne hover:bg-champagne hover:text-ink-950 transition-colors text-[11px] tracking-[0.3em] uppercase"
          >
            Bekijk het volledige aanbod
          </button>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div data-testid="detail-page" className="min-h-screen bg-ink-950">
      <Header />
      <main>
        {/* Breadcrumb */}
        <div className="max-w-[1440px] mx-auto px-6 md:px-10 lg:px-12 pt-8">
          <Link
            to="/aanbod"
            data-testid="detail-back-link"
            className="inline-flex items-center gap-2 text-[11px] tracking-[0.25em] uppercase text-zinc-500 hover:text-champagne transition-colors font-body"
          >
            <ChevronLeft size={14} />
            Terug naar aanbod
          </Link>
        </div>

        {/* Gallery */}
        <section className="max-w-[1440px] mx-auto px-6 md:px-10 lg:px-12 mt-6">
          <div className="relative w-full aspect-[16/9] md:aspect-[21/9] overflow-hidden bg-ink-900 border border-white/[0.06]">
            <img
              src={vehicle.image}
              alt={`${vehicle.brand} ${vehicle.model}`}
              className="w-full h-full object-cover"
              loading="eager"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-ink-950/60 via-transparent to-transparent" />
            <div className="absolute top-6 left-6 px-3 py-1.5 text-[10px] tracking-[0.28em] uppercase bg-ink-950/80 backdrop-blur border border-white/10 text-champagne font-body">
              {vehicle.category}
            </div>
          </div>

          {/* Small thumbnails row (placeholder for future full gallery) */}
          <div className="mt-3 grid grid-cols-4 gap-3">
            {[0, 1, 2, 3].map((i) => (
              <div
                key={i}
                className={`aspect-[3/2] bg-ink-900 border ${
                  i === 0 ? "border-champagne/40" : "border-white/[0.06]"
                } overflow-hidden opacity-${i === 0 ? "100" : "60"} hover:opacity-100 transition-opacity cursor-pointer`}
              >
                <img
                  src={vehicle.image}
                  alt={`${vehicle.brand} ${vehicle.model} view ${i + 1}`}
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              </div>
            ))}
          </div>
        </section>

        {/* Title & CTAs */}
        <section className="max-w-[1440px] mx-auto px-6 md:px-10 lg:px-12 mt-12 md:mt-16 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
          <div className="lg:col-span-7">
            <p className="text-[11px] tracking-[0.3em] uppercase text-champagne font-body mb-3">
              {vehicle.brand}
            </p>
            <h1
              data-testid="detail-title"
              className="font-display text-4xl md:text-5xl lg:text-6xl text-white leading-[1.05] tracking-tight"
            >
              {vehicle.title}
            </h1>
            <div className="mt-8 grid grid-cols-2 md:grid-cols-5 gap-6 hairline-t pt-8">
              <HeaderSpec
                icon={<Calendar size={14} />}
                label="Bouwjaar"
                value={vehicle.year_display}
              />
              <HeaderSpec
                icon={<Gauge size={14} />}
                label="Km-stand"
                value={vehicle.mileage_display}
              />
              <HeaderSpec
                icon={<Zap size={14} />}
                label="Vermogen"
                value={`${vehicle.power_pk} PK`}
              />
              <HeaderSpec
                icon={<Wind size={14} />}
                label="Top"
                value={`${vehicle.top_speed} km/h`}
              />
              <HeaderSpec
                icon={<Timer size={14} />}
                label="0–100"
                value={`${vehicle.acceleration.toString().replace(".", ",")} s`}
              />
            </div>
          </div>

          <aside
            data-testid="detail-cta-panel"
            className="lg:col-span-5 bg-ink-900 border border-white/[0.06] p-8 md:p-10 h-fit"
          >
            <p className="text-[11px] tracking-[0.3em] uppercase text-zinc-500 font-body">
              Vraagprijs
            </p>
            <div className="flex items-baseline gap-3 mt-2">
              <span
                data-testid="detail-price"
                className="font-display text-5xl md:text-6xl text-white tracking-tight leading-none"
              >
                {vehicle.price_formatted}
              </span>
            </div>
            <p className="mt-3 text-xs text-zinc-500 font-body">
              Inclusief BTW · beschikbaar voor export
            </p>

            <div className="mt-8 flex flex-col gap-3">
              <button
                type="button"
                data-testid="cta-plan-viewing"
                onClick={() => {
                  const el = document.getElementById("contact-section");
                  if (el) el.scrollIntoView({ behavior: "smooth" });
                }}
                className="w-full py-4 bg-champagne text-ink-950 hover:bg-champagne-soft transition-colors text-[11px] tracking-[0.3em] uppercase font-body font-semibold"
              >
                Plan een bezichtiging
              </button>
              <a
                href="tel:+31201234567"
                data-testid="cta-call-direct"
                className="w-full py-4 border border-white/15 text-white hover:border-champagne hover:text-champagne transition-colors flex items-center justify-center gap-2 text-[11px] tracking-[0.3em] uppercase font-body"
              >
                <Phone size={13} />
                Bel direct
              </a>
              <a
                href="https://wa.me/31201234567"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="cta-whatsapp"
                className="w-full py-4 border border-white/15 text-white hover:border-champagne hover:text-champagne transition-colors flex items-center justify-center gap-2 text-[11px] tracking-[0.3em] uppercase font-body"
              >
                <MessageCircle size={13} />
                WhatsApp
              </a>
            </div>
          </aside>
        </section>

        {/* Sections: Specs / Options / Description */}
        <section className="max-w-[1440px] mx-auto px-6 md:px-10 lg:px-12 mt-20 md:mt-28 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
          {/* Specs */}
          <div className="lg:col-span-6">
            <SectionTitle label="01" title="Specificaties" />
            <div className="mt-8 border-t border-white/[0.06]">
              {[
                ["Merk", vehicle.brand],
                ["Model", vehicle.model],
                ["Categorie", vehicle.category],
                ["Bouwjaar", vehicle.year_display],
                ["Kilometerstand", vehicle.mileage_display],
                ["Vermogen", `${vehicle.power_kw} kW (${vehicle.power_pk} PK)`],
                ["Topsnelheid", `${vehicle.top_speed} km/h`],
                ["Acceleratie", `${vehicle.acceleration.toString().replace(".", ",")} s (0–100)`],
                ["Transmissie", vehicle.transmission],
                ["Brandstof", vehicle.fuel],
                ["Exterieurkleur", vehicle.color],
              ].map(([label, value]) => (
                <div
                  key={label}
                  className="flex items-center justify-between py-4 border-b border-white/[0.06]"
                >
                  <span className="text-[11px] tracking-[0.25em] uppercase text-zinc-500 font-body">
                    {label}
                  </span>
                  <span className="text-sm text-white font-body font-medium">
                    {value}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Options */}
          <div className="lg:col-span-6">
            <SectionTitle label="02" title="Opties & Uitrusting" />
            <ul className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-3">
              {vehicle.options.map((o) => (
                <li
                  key={o}
                  className="flex items-start gap-3 py-3 px-4 bg-ink-900 border border-white/[0.06]"
                >
                  <Check
                    size={14}
                    className="mt-0.5 text-champagne shrink-0"
                  />
                  <span className="text-sm text-zinc-200 font-body">{o}</span>
                </li>
              ))}
            </ul>

            {/* Description */}
            <div className="mt-12">
              <SectionTitle label="03" title="Omschrijving" />
              <p className="mt-8 text-base text-zinc-300 font-body leading-relaxed">
                {vehicle.description}
              </p>
            </div>

            {/* Financing */}
            <div className="mt-12">
              <SectionTitle label="04" title="Financiering & Inruil" />
              <p className="mt-6 text-sm text-zinc-400 font-body leading-relaxed">
                Wij werken samen met gespecialiseerde financiers voor zowel
                particuliere als zakelijke financiering op maat. Ook inruil van
                uw huidige voertuig behoort tot de mogelijkheden — onze taxateur
                komt graag persoonlijk bij u langs.
              </p>
              <ul className="mt-6 flex flex-col gap-2">
                {[
                  "Financiering op maat (rente vanaf 5,9%)",
                  "Operational & financial lease mogelijk",
                  "Inruil — transparante taxatie binnen 24 uur",
                  "Export-documentatie & transport Europa-breed",
                ].map((t) => (
                  <li
                    key={t}
                    className="flex items-start gap-3 text-sm text-zinc-300 font-body"
                  >
                    <span className="text-champagne">·</span>
                    <span>{t}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        {/* Contact */}
        <section
          id="contact-section"
          data-testid="detail-contact-section"
          className="max-w-[1440px] mx-auto px-6 md:px-10 lg:px-12 mt-24 md:mt-32"
        >
          <div className="max-w-2xl">
            <SectionTitle label="05" title="Persoonlijk contact" />
            <p className="mt-6 text-base text-zinc-400 font-body leading-relaxed mb-10">
              Interesse in de {vehicle.title}? Laat uw gegevens achter en wij
              nemen discreet contact met u op voor een afspraak op locatie of
              bij u op uitnodiging.
            </p>
          </div>
          <div className="max-w-3xl">
            <ContactForm vehicleTitle={vehicle.title} />
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

const SectionTitle = ({ label, title }) => (
  <div>
    <div className="flex items-center gap-4 mb-4">
      <span className="text-[11px] tracking-[0.3em] uppercase text-champagne font-body">
        {label}
      </span>
      <span className="h-px flex-1 bg-white/[0.08]" />
    </div>
    <h2 className="font-display text-3xl md:text-4xl text-white tracking-tight">
      {title}
    </h2>
  </div>
);

const HeaderSpec = ({ icon, label, value }) => (
  <div className="flex flex-col gap-1">
    <span className="flex items-center gap-2 text-[9px] tracking-[0.3em] uppercase text-zinc-500 font-body">
      <span className="text-champagne-muted">{icon}</span>
      {label}
    </span>
    <span className="font-display text-xl md:text-2xl text-white">{value}</span>
  </div>
);

export default DetailPage;
